<!-- Copyright: Seán I. O'Donoghue -->
<!-- The poster gallery (2026-10-26), replacing the old server's www.vizbi.org/Posters/ list: -->
<!-- every published poster, newest year first, as thumbnails sized to fill the screen (as the -->
<!-- old list did) or as a list; filtered by year (/Posters/2025) and category (?category=). -->
<!-- Each thumbnail keeps its own place before it loads (its width and height, posters.json). -->
<!-- A poster opens on its own page (PosterPage.vue, /Posters/<year>/<poster>). -->
<template>
  <PageHeader
    title="Posters" to="/Posters" :count="posters.length ? shown.length : ''" :total="countTotal">
    <!-- arrived from the year's page (/<year>/Posters): the way back, in the poster page's
         style, until the person moves on (Seán, 2026-11-01; useYearArrival.js) -->
    <template v-if="backYear" #before>
      <a
        :href="`/${backYear}`"
        class="back"
        :aria-label="`Back to VIZBI ${backYear}`"
        :title="`Back to VIZBI ${backYear}`"
        @click.prevent="router.push(`/${backYear}`)">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path :d="BACK_ICON" /></svg>
      </a>
    </template>
    <!-- the view first, left of the filters (Seán, 2026-10-26) -->
    <SegmentedControl v-model="view" :options="VIEWS" label="Show the posters as" />
    <!-- the posters shown, as a slideshow, full screen (PosterSlideshow.vue) -->
    <ButtonBespoke v-if="shown.length" id="slideshow" title="Slideshow" aria-label="Slideshow"
      @click="openSlideshow">
      <!-- an original icon: two stacked slides, a play triangle on the front one -->
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path
          d="M7.5 8.5V6A1.5 1.5 0 0 1 9 4.5h10.5A1.5 1.5 0 0 1 21 6v8.5A1.5 1.5 0 0 1 19.5 16H18"
          fill="none" stroke="currentColor" stroke-width="1.75" stroke-linejoin="round" />
        <rect x="3" y="8.5" width="15" height="11" rx="1.5"
          fill="none" stroke="currentColor" stroke-width="1.75" />
        <path d="M9 11.5v5l4.25-2.5z" fill="currentColor" />
      </svg>
    </ButtonBespoke>
    <ButtonBespoke v-if="year || category" id="showAll" @click="showAll">Show All</ButtonBespoke>
    <!-- a filter chosen: Show All and its tag only, the other menu aside (the standard; Seán,
         2026-10-26); both show when both are chosen (e.g. from an address) -->
    <Dropdown
      v-if="!category || year"
      chevron
      placeholder="Year"
      :options="years"
      :selected="year"
      @update="filterByYear" />
    <Dropdown
      v-if="!year || category"
      chevron
      placeholder="Category"
      :options="categories"
      :separators="categorySeparators"
      :selected="category"
      @update="filterByCategory" />
  </PageHeader>
  <p v-if="!posters.length" class="status">Loading the posters…</p>
  <div v-else-if="view === 'Gallery'" ref="gallery" class="thumbnails">
    <a
      v-for="poster in shown"
      :key="poster.url"
      :href="getPosterPath(poster)"
      :title="`${poster.posterNum} - ${poster.title}`"
      @click.prevent="openPoster(poster)">
      <img
        loading="lazy"
        :src="getThumbnailUrl(poster, rowHeight)"
        :width="poster.width"
        :height="poster.height"
        :style="{height: `${rowHeight}px`}"
        :class="{isWaiting: !loaded.has(poster.url)}"
        :alt="`Poster ${poster.posterNum}: ${poster.title}`"
        @load="loaded.add(poster.url)" />
    </a>
  </div>
  <ol v-else class="list">
    <li v-for="poster in shown" :key="poster.url">
      <a :href="getPosterPath(poster)" class="number" @click.prevent="openPoster(poster)">
        {{ poster.posterNum }}
      </a>
      <div>
        <a :href="getPosterPath(poster)" class="title" @click.prevent="openPoster(poster)">
          {{ poster.title }}
        </a>
        <p class="authors">
          {{ poster.authors }}<span v-if="poster.address"> ({{ poster.address }})</span>
        </p>
      </div>
    </li>
  </ol>
  <PosterSlideshow v-if="isSlideshowOpen" :posters="shown" @close="isSlideshowOpen = false" />
</template>

<script setup>
import {computed, onBeforeUnmount, onMounted, reactive, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import ButtonBespoke from "@/components/ButtonBespoke.vue";
import PosterSlideshow from "@/components/PosterSlideshow.vue";
import PageHeader from "@/components/PageHeader.vue";
import SegmentedControl from "@/components/App/SegmentedControl.vue";
import Dropdown from "@/components/Dropdown.vue";
import {getCategory, getThumbnailUrl, usePosters} from "@/composables/usePosters.js";
import useYearArrival, {clearYearArrival} from "@/composables/useYearArrival.js";

const MIN_ROW = 24; // px: the smallest thumbnail that still reads as a poster
const MAX_ROW = 600; // px: the large thumbnails' own height
const FILL = 0.5; // of the screen's area, as the old gallery's 'fudge factor'

const route = useRoute();
const router = useRouter();
const {posters, years, categories, categorySeparators} = usePosters();
const VIEWS = ["Gallery", "List"]; // the thumbnails, or a list of their numbers and titles
const [view, gallery, screenArea] = [ref("Gallery"), ref(null), ref(0)];
const loaded = reactive(new Set()); // the thumbnails that have arrived: no longer waiting

// the posters' row height, so that those shown fill about half the screen (the old list's rule)
function getRowHeight(count) {
  if (!count || !screenArea.value) return 100;
  const height = Math.sqrt((FILL * screenArea.value) / count);
  return Math.round(Math.min(MAX_ROW, Math.max(MIN_ROW, height)));
}

function updateScreenArea() {
  const width = gallery.value?.clientWidth || document.documentElement.clientWidth;
  screenArea.value = width * window.innerHeight;
}

const getQuery = (newCategory) => (newCategory ? {category: newCategory} : {});
const getPath = (newYear) => (newYear ? `/Posters/${newYear}` : "/Posters");
const goTo = (newYear, newCategory) => {
  router.push({path: getPath(newYear), query: getQuery(newCategory)});
};

const getIsYear = (part) => /^\d{4}$/.test(part);
const year = computed(() => [route.params.specification].flat().find(getIsYear) || "");
// the way back to the year arrived from (PosterPage.vue's arrow), while the address is still
// the one arrived at; any change of address (a filter, a poster, another page) ends it
const BACK_ICON = "M20 11H7.8l5.6-5.6L12 4l-8 8 8 8 1.4-1.4L7.8 13H20z";
const arrivedFromYear = useYearArrival();
const backYear = computed(() => (arrivedFromYear.value === year.value ? year.value : ""));
watch(() => route.fullPath, clearYearArrival);
onBeforeUnmount(clearYearArrival); // leaving for another page (the arrow itself included)
// an old address's category (?category=Genomes) as its new one (DNA)
const category = computed(() => (route.query.category ? getCategory(route.query.category) : ""));
const shown = computed(() => posters.value.filter((poster) => {
  return (!year.value || poster.year === year.value)
    && (!category.value || poster.category === category.value);
}));
const rowHeight = computed(() => getRowHeight(shown.value.length));
// the whole collection beside the posters shown, while a filter is on ("[47/1,076]")
const countTotal = computed(() => {
  return shown.value.length === posters.value.length ? "" : posters.value.length;
});

const filterByYear = (option) => goTo(option, category.value);
const filterByCategory = (option) => goTo(year.value, option);
const showAll = () => goTo("", "");
const getPosterPath = (poster) => `/Posters/${poster.year}/${poster.posterNum}`;
const openPoster = (poster) => router.push(getPosterPath(poster));

// full screen asked for within the click (browsers allow it only then); the whole page, the
// slideshow over it. Where there's none (an iPhone), the slideshow fills the window
const isSlideshowOpen = ref(false);
function openSlideshow() {
  document.documentElement.requestFullscreen?.().catch(() => {});
  isSlideshowOpen.value = true;
}

onMounted(() => {
  updateScreenArea();
  window.addEventListener("resize", updateScreenArea);
});
onBeforeUnmount(() => window.removeEventListener("resize", updateScreenArea));
</script>

<style scoped>
/* the way back to the year: PosterPage.vue's .back (its hover: a little brighter, never green) */
.back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  margin-right: 0.5rem;
  border-radius: 4px;
  color: var(--text-color);
  --hover-text: black;
  --hover-background: var(--filter-control-background);
}
/* the whole selector global: Vue would keep only .darkMode (DESIGN.md § 15) */
:global(.darkMode .pageHeader .back) {
  --hover-text: white;
  --hover-background: transparent;
}
.back:hover {
  color: var(--hover-text);
  background: var(--hover-background);
}
.back svg {
  width: 1.5rem;
  height: 1.5rem;
  fill: currentColor;
}
#slideshow {
  min-width: 0; /* the icon alone (Seán, 2026-10-26), not ButtonBespoke's 6rem */
}
#slideshow svg {
  display: block;
  width: 1.5em;
  height: 1.5em;
}
.status {
  color: #777;
}
.thumbnails {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 1rem;
}
.thumbnails a {
  display: block;
  line-height: 0;
}
.thumbnails img {
  width: auto; /* from each thumbnail's own width and height: its place kept before it loads */
  color: transparent; /* not its alt text while it loads: the waiting grey (global.css) */
}
.list {
  list-style: none;
  padding: 0;
  margin-top: 1rem;
}
.list li {
  display: flex;
  gap: 0.75rem;
  padding: 0.4rem 0;
  border-bottom: 1px solid #eee;
}
.list .number {
  min-width: 3rem;
  font-weight: 600;
}
.list .authors {
  margin: 0.15rem 0 0;
  font-size: 0.85em;
  color: #555;
}
</style>
